import React, { useState } from 'react';
import { CLASSES, ASSETS, ENEMIES, BOSS } from '../mock';
import { Lock, Skull, Flame } from 'lucide-react';

const envItems = [
  { src: ASSETS.envGrass, name: 'Grasslands', status: 'live' },
  { src: ASSETS.envSnow, name: 'Frozen Tundra', status: 'live' },
  { src: null, name: 'Sunken Dunes', status: 'upcoming' },
  { src: null, name: 'Crystal Caves', status: 'upcoming' },
];

const AboutPage = () => {
  const [selected, setSelected] = useState(0);

  return (
    <div className="pt-28 pb-20 bg-[#0a0608] min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-12 text-center fade-up">
          <p className="font-nav text-[#f5b400] text-xs tracking-widest">ABOUT THE GAME</p>
          <h1 className="font-heading section-heading-white mt-3">EXPLORE A DIVERSE</h1>
          <h1 className="font-heading section-heading-gold">ENVIRONMENT</h1>
          <div className="mx-auto mt-6 h-1 w-24 bg-[#f5b400]" />
        </div>

        {/* Environments */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6 mb-24">
          {envItems.map((env, i) => {
            const upcoming = env.status === 'upcoming';
            return (
              <div
                key={i}
                className={`env-card rounded-lg overflow-hidden border ${
                  upcoming ? 'border-[#f5b400]/30 bg-black/60' : 'border-white/10 bg-black'
                }`}
              >
                <div className="aspect-video overflow-hidden relative flex items-center justify-center bg-[#0a0608]">
                  {upcoming ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-[linear-gradient(135deg,#1a1410_0%,#0a0608_100%)]">
                      <div
                        className="absolute inset-0 opacity-20"
                        style={{
                          backgroundImage:
                            'repeating-linear-gradient(45deg, #f5b400 0 2px, transparent 2px 16px)',
                        }}
                        aria-hidden="true"
                      />
                      <Lock size={40} className="text-[#f5b400] mb-2 relative z-10" />
                      <span className="font-heading text-white/90 text-lg md:text-xl tracking-wider relative z-10">
                        COMING SOON
                      </span>
                      <span className="font-nav text-[#f5b400] text-[9px] mt-1 tracking-widest relative z-10">
                        MAP IN DEVELOPMENT
                      </span>
                    </div>
                  ) : (
                    <img
                      src={env.src}
                      alt={env.name}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="px-5 py-4 flex items-center justify-between">
                  <span className="font-heading text-white text-lg">{env.name}</span>
                  <span
                    className={`font-nav text-xs ${
                      upcoming ? 'text-white/40' : 'text-[#f5b400]'
                    }`}
                  >
                    {upcoming ? 'UPCOMING' : 'EXPLORE'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Classes */}
        <div className="mb-12 text-center">
          <h2 className="font-heading section-heading-white">CHOOSE</h2>
          <h2 className="font-heading section-heading-gold">YOUR ALTER EGO !</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
          {CLASSES.map((c, i) => {
            const isActive = selected === i;
            return (
              <button
                key={c.name}
                onClick={() => setSelected(i)}
                className={`char-card relative group rounded-xl p-6 flex flex-col items-center text-center border transition-all duration-300 ${
                  isActive
                    ? 'border-[#f5b400] bg-black/55'
                    : 'border-white/15 bg-black/35 hover:border-[#f5b400]/60'
                }`}
              >
                <div
                  className="absolute inset-x-6 -top-3 h-1 rounded-full"
                  style={{ background: c.color, opacity: isActive ? 1 : 0.5 }}
                />
                <img
                  src={c.img}
                  alt={c.name}
                  className={`w-40 h-40 md:w-52 md:h-52 object-contain ${i % 2 === 0 ? 'float-anim' : 'float-anim-2'}`}
                  draggable="false"
                />
                <h3 className="font-heading text-2xl text-white mt-2">{c.name.toUpperCase()}</h3>
                <p className="font-body text-sm text-white/70 mt-2">{c.description}</p>
                <span
                  className="mt-4 font-nav text-xs"
                  style={{ color: isActive ? '#f5b400' : '#ffffff99' }}
                >
                  {isActive ? 'SELECTED' : 'SELECT'}
                </span>
              </button>
            );
          })}
        </div>

        <p className="mt-12 text-center text-white/80 font-body max-w-3xl mx-auto">
          Choose from four elemental classes — <em className="text-[#f5b400]">Earth</em>,{' '}
          <em className="text-[#f5b400]">Fire</em>, Air, and Water — each with unique abilities
          and playstyles. Master your craft, harness the power of the elements, and conquer the realm!
        </p>

        {/* Foes example */}
        <div className="mt-24">
          <div className="mb-10 text-center">
            <p className="font-nav text-[#f5b400] text-xs tracking-widest">BESTIARY</p>
            <h2 className="font-heading section-heading-white mt-3">FACE THE</h2>
            <h2 className="font-heading section-heading-gold">FOES OF ELDORIA</h2>
            <p className="mt-4 max-w-2xl mx-auto text-white/75 font-body text-sm">
              A glimpse at the creatures stalking Map 2. Study their tells, exploit their weaknesses,
              and bring back their loot.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {ENEMIES.map((e) => (
              <div
                key={e.name}
                className="group relative rounded-xl border border-white/12 bg-[radial-gradient(ellipse_at_top,#1a1410_0%,#0a0608_100%)] p-4 flex flex-col items-center text-center hover:border-[#f5b400]/60 transition-all hover:-translate-y-1"
              >
                <span className="absolute top-2 right-2 font-nav text-[10px] tracking-widest text-[#f5b400] bg-[#f5b400]/10 border border-[#f5b400]/40 rounded-full px-2 py-0.5">
                  LV {e.level}
                </span>
                <div className="w-full aspect-square flex items-center justify-center mb-3">
                  <img
                    src={e.img}
                    alt={e.name}
                    className="max-w-[80%] max-h-[80%] object-contain group-hover:scale-110 transition-transform duration-500"
                    draggable="false"
                  />
                </div>
                <h4 className="font-heading text-white text-base">{e.name}</h4>
                <span className="font-nav text-[10px] text-white/55 tracking-widest mt-1 flex items-center gap-1">
                  <Flame size={10} className="text-[#f5b400]" />
                  {e.element.toUpperCase()}
                </span>
              </div>
            ))}
          </div>

          {/* Boss highlight */}
          <div className="mt-8 rounded-xl border border-red-500/30 bg-gradient-to-r from-red-950/40 via-black/60 to-black/60 p-6 flex flex-col md:flex-row items-center gap-6">
            <div className="w-32 h-32 flex items-center justify-center shrink-0">
              <img
                src={BOSS.img}
                alt={BOSS.name}
                className="max-w-full max-h-full object-contain drop-shadow-[0_0_20px_rgba(239,68,68,0.4)]"
                draggable="false"
              />
            </div>
            <div className="flex-1 text-center md:text-left">
              <span className="inline-flex items-center gap-1 font-nav text-[10px] tracking-widest text-red-300 bg-red-700/30 border border-red-500/40 rounded-full px-2 py-0.5">
                <Skull size={10} /> WORLD BOSS
              </span>
              <h3 className="font-heading text-white text-2xl mt-2">{BOSS.name}</h3>
              <p className="font-body text-sm text-white/75 mt-2">
                A towering brute that guards the deepest reaches of Map 2. Gather a party of four
                or perish in the dust.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
